// src/contexts/ThemeContext.jsx
import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Create Context
const ThemeContext = createContext();

// Custom hook (renamed to avoid conflict with MUI)
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within CustomThemeProvider");
  }
  return context;
};

export const CustomThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Persist theme
  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
  };

  // Memoize theme (important for performance)
 const theme = useMemo(
  () =>
    createTheme({
      palette: {
        mode: darkMode ? "dark" : "light",

        ...(darkMode
          ? {
              // ✅ TRUE BLACK MODE
              primary: { main: "#ffffff" }, // white accent instead of blue
              secondary: { main: "#a1a1aa" },

              background: {
                default: "#000000",   // full black
                paper: "#0a0a0a",     // slight elevation
              },

              text: {
                primary: "#ffffff",
                secondary: "#a1a1aa",
              },

              divider: "#1f1f1f",
            }
          : {
              // ✅ CLEAN LIGHT MODE (no heavy blue)
              primary: { main: "#111827" }, // near black
              secondary: { main: "#4b5563" },

              background: {
                default: "#f9fafb",
                paper: "#ffffff",
              },

              text: {
                primary: "#111827",
                secondary: "#6b7280",
              },

              divider: "#e5e7eb",
            }),
      },

      shape: {
        borderRadius: 12,
      },

      typography: {
        fontFamily: '"Inter", "Poppins", sans-serif',
      },

      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: darkMode ? "#000000" : "#f9fafb",
            },
          },
        },

        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none", // ❌ remove MUI gradient tint
            },
          },
        },

        MuiCard: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
            },
          },
        },

        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: darkMode ? "#000000" : "#ffffff",
              backgroundImage: "none",
              boxShadow: "none",
            },
          },
        },

        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              borderRadius: 10,
            },
          },
        },
      },
    }),
  [darkMode]
);
  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};